import React, { useEffect, useState } from 'react';
import { Psychology } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { toast } from 'react-toastify';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-llm`;

/** Same auth header pattern as GlobalUsers / TenantAdmin (gateway requires Bearer). */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const MasterLlm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const isCdxUser = () => {
    try {
      return localStorage.getItem('cdx_user') === 'true';
    } catch {
      return false;
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}?includeInactive=false`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const mapped = (Array.isArray(data) ? data : []).map((item) => ({
        ...item,
        id: item.masterLlmId || item.master_llm_id,
        enabledLabel: item.enabled !== false ? 'Yes' : 'No',
      }));
      setRows(mapped);
      return mapped;
    } catch (error) {
      toast.error(`Error fetching Master LLMs: ${error.message}`);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      const body = {
        provider: data.provider,
        modelCode: data.modelCode,
        displayName: data.displayName,
        invocationKind: data.invocationKind || 'CHAT',
        defaultConnectionConfig: data.defaultConnectionConfig || '{}',
        enabled: data.enabled !== false && data.enabled !== 'false',
      };
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      toast.success('Master LLM created');
      await fetchRows();
      return response.json();
    } catch (error) {
      toast.error(`Error creating Master LLM: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const body = {
        provider: data.provider,
        modelCode: data.modelCode,
        displayName: data.displayName,
        invocationKind: data.invocationKind || 'CHAT',
        defaultConnectionConfig: data.defaultConnectionConfig || '{}',
        enabled: data.enabled !== false && data.enabled !== 'false',
      };
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      toast.success('Master LLM updated');
      await fetchRows();
      return response.json();
    } catch (error) {
      toast.error(`Error updating Master LLM: ${error.message}`);
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      toast.success('Master LLM removed');
      await fetchRows();
      return true;
    } catch (error) {
      toast.error(`Error deleting Master LLM: ${error.message}`);
      throw error;
    }
  };

  const columns = [
    { field: 'provider', headerName: 'Provider', width: 140 },
    { field: 'modelCode', headerName: 'Model code', width: 180 },
    { field: 'displayName', headerName: 'Display name', width: 220 },
    { field: 'invocationKind', headerName: 'Invocation', width: 120 },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.enabled !== false ? 'Yes' : 'No'}
          color={params.row.enabled !== false ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
  ];

  const formFields = [
    {
      name: 'provider',
      label: 'Provider',
      required: true,
      type: 'select',
      options: ['OpenAI', 'Anthropic', 'Google', 'AzureOpenAI', 'Other'],
      validate: (value) => (!value ? 'Provider is required' : null),
    },
    {
      name: 'modelCode',
      label: 'Model code',
      required: true,
      validate: (value) => (!value ? 'Model code is required' : null),
    },
    {
      name: 'displayName',
      label: 'Display name',
    },
    {
      name: 'invocationKind',
      label: 'Invocation kind (hub registry)',
      type: 'select',
      options: [
        'anthropic_messages',
        'openai_compatible_chat',
        'ollama_chat',
        'groq_openai',
      ],
      validate: (value) => (!value ? 'Invocation kind is required' : null),
    },
    {
      name: 'defaultConnectionConfig',
      label: 'Default connection_config (JSON)',
      multiline: true,
      validate: (value) => {
        if (!value || !String(value).trim()) return null;
        try {
          JSON.parse(value);
          return null;
        } catch {
          return 'Must be valid JSON';
        }
      },
    },
    {
      name: 'enabled',
      label: 'Enabled',
      type: 'checkbox',
    },
  ];

  const initialFormData = {
    provider: 'OpenAI',
    modelCode: '',
    displayName: '',
    invocationKind: 'openai_compatible_chat',
    defaultConnectionConfig:
      '{\n  "provider": "OpenAI",\n  "model": "",\n  "base_url": ""\n}',
    enabled: true,
  };

  return (
    <DynamicContainer
      title="Master LLM"
      icon={Psychology}
      columns={columns}
      formFields={formFields}
      initialFormData={initialFormData}
      idField="id"
      rows={rows}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isLoading={isLoading}
      isCdxUser={isCdxUser()}
      functionCode="Master LLM"
      prepareEditData={(row) => ({
        ...row,
        defaultConnectionConfig:
          typeof row.defaultConnectionConfig === 'string'
            ? row.defaultConnectionConfig
            : JSON.stringify(row.defaultConnectionConfig || {}, null, 2),
        enabled: row.enabled !== false,
      })}
    />
  );
};

export default MasterLlm;
